import React from "react";
import { Header } from "antd/es/layout/layout";
import { Avatar, Dropdown, MenuProps, Space, Tooltip } from "antd";
import { 
  AppstoreAddOutlined, 
  MenuUnfoldOutlined, 
  UserOutlined, 
  SafetyCertificateOutlined, 
  WarningOutlined, 
  DownOutlined 
} from "@ant-design/icons";

export interface TopBarProps {
  user: {
    name: string;
    permissions: {
      views: string[];
    };
  };
  company: {
    current: {
      uid: string;
      name: string;
      alias?: string;
      image?: string;
      icon?: string;
      modules?: Array<{
        name: string;
        path: string;
        icon?: string;
      }>;
      subscription?: {
        status?: string;
        end_date: string;
        plans?: {
          name: string;
        };
      };
    };
    list?: Array<{
      uid: string;
      name: string;
      image?: string;
      icon?: string;
    }>;
  };
  config: {
    isMobile: boolean;
    sideVisible: boolean;
  };
  userMenuItems: MenuProps['items'];
  onToggleMobileMenu: (e: React.MouseEvent) => void;
  onCompanyChange: (key: string) => void;
  onModuleClick: (key: string) => void;
  onUserItemClick: (key: string) => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  user,
  company,
  config,
  userMenuItems,
  onToggleMobileMenu,
  onCompanyChange,
  onModuleClick,
  onUserItemClick,
}) => {
  const itemsCompany: MenuProps['items'] = company.current.modules?.map((e, i) => ({
    key: i.toString(),
    label: e.name,
    icon: <Avatar src={e.icon} />,
  })) || [];

  if (itemsCompany.length > 0) {
    itemsCompany.unshift(
      { key: "100", label: "Modulos", disabled: true },
      { type: 'divider' as const }
    );
  }

  const itemsListCompanies: MenuProps['items'] = company.list?.map((e) => ({
    key: e.uid,
    label: e.name,
    icon: <Avatar src={e.icon || e.image} size="small" />,
  })) || [];
  const renderSubscriptionStatus = () => {
    if (!company.current.subscription) {
      return (
        <Tooltip title="Sin suscripción configurada">
          <WarningOutlined style={{ color: "#f5222d", fontSize: "1.25rem", cursor: "pointer" }} />
        </Tooltip>
      );
    }

    const { status, end_date, plans } = company.current.subscription;
    const isActive = status?.toUpperCase() === 'ACTIVE' && new Date(end_date) >= new Date();
    const formattedDate = new Date(end_date).toLocaleDateString();

    if (isActive) {
      return (
        <Tooltip title={`Suscripción Activa: ${plans?.name || 'Plan'} (Válida hasta: ${formattedDate})`}>
          <SafetyCertificateOutlined style={{ color: "#52c41a", fontSize: "1.25rem", cursor: "pointer" }} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`Suscripción Vencida (Venció el: ${formattedDate})`}>
        <WarningOutlined style={{ color: "#faad14", fontSize: "1.25rem", cursor: "pointer" }} />
      </Tooltip>
    );
  };

  return (
    <Header style={{ 
      padding: "0 16px", background: "#fff", display: "flex", 
      alignItems: "center", justifyContent: "space-between", marginBottom: "1rem",
      boxShadow: "0 .15rem 1.75rem 0 rgba(58,59,69,.15)!important" 
    }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {config.isMobile && (
          <div className="icon-continer-top">
            <a href="/logo/menu" onClick={onToggleMobileMenu}>
              <Space>
                <MenuUnfoldOutlined style={{ color: "black" }} />
              </Space>
            </a>
          </div>
        )}
        <Dropdown 
          menu={{ items: itemsListCompanies, onClick: ({ key }) => onCompanyChange(key) }} 
          trigger={['click']} 
          disabled={!company.list || company.list.length <= 1}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: (company.list && company.list.length > 1) ? 'pointer' : 'default' 
          }}>
            <Avatar src={company.current.icon || company.current.image} style={{ marginLeft: config.isMobile ? 10 : 0 }} />
            {!config.isMobile && (
              <h3 style={{ 
                marginLeft: 12, color: "#5a5c69", fontSize: "clamp(1.2rem, 3.5vw, 1.75rem)", margin: 0, 
                lineHeight: 1, display: 'flex', alignItems: 'center', gap: '8px',
                maxWidth: 'min(400px, 40vw)', minWidth: 0
              }}>
                <span style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  flex: 1
                }}>
                  {company.current.alias || company.current.name}
                </span>
                {company.list && company.list.length > 1 && <DownOutlined style={{ fontSize: '0.8rem', color: '#b7b9cc', flexShrink: 0 }} />}
              </h3>
            )}
          </div>
        </Dropdown>
      </div>

      <Space size="middle" style={{ display: "flex", alignItems: "center" }}>
        <div className="icon-continer-top">
          <Dropdown menu={{ items: itemsCompany, onClick: ({ key }) => onModuleClick(key) }} trigger={['click']}>
            <a href="/logo/apps" onClick={(e) => e.preventDefault()}>
              <Space>
                <AppstoreAddOutlined style={{ color: "black" }} />
              </Space>
            </a>
          </Dropdown>
        </div>

        <div className="icon-continer-top" style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
          {renderSubscriptionStatus()}
        </div>
        <div style={{
          width: 0,
          borderRight: "1px solid #e3e6f0",
          height: "calc(4.375rem - 2rem)",
        }}></div>
        {!config.isMobile && (
          <span style={{ 
            maxWidth: '150px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            display: 'inline-block',
            verticalAlign: 'middle'
          }}>
            {user.name}
          </span>
        )}
        <div className="icon-continer-top">
          <Dropdown menu={{ items: userMenuItems, onClick: ({ key }) => onUserItemClick(key) }} trigger={['click']}>
            <a href="/logo/user" onClick={(e) => e.preventDefault()}>
              <Space>
                <UserOutlined style={{ color: "black" }} />
              </Space>
            </a>
          </Dropdown>
        </div>
      </Space>
    </Header>
  );
};
